import { useTranslation } from 'react-i18next';
import { DownloadSimple, ClockCounterClockwise } from '@phosphor-icons/react';
import type { DocumentVersionDto } from '@monorepo/shared';
import { DocumentFileIcon } from './DocumentFileIcon';

interface DocumentVersionsTableProps {
  versions: DocumentVersionDto[];
  isLoading: boolean;
  isDownloading: boolean;
  onDownload: (version: DocumentVersionDto) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
};

const formatDate = (date: Date): string =>
  new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const DocumentVersionsTable = ({
  versions,
  isLoading,
  isDownloading,
  onDownload,
}: DocumentVersionsTableProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin">
          <ClockCounterClockwise size={32} className="text-primary" />
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ClockCounterClockwise size={64} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">
          {t('documents.document_versions.empty')}
        </h3>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.old_versions.table.version')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.old_versions.table.file_name')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.old_versions.table.uploaded_by')}
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
              {t('documents.old_versions.table.uploaded_at')}
            </th>
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
              {t('documents.old_versions.table.download')}
            </th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                    v{version.versionNumber}
                  </span>
                  {version.isLatest && (
                    <span className="text-xs text-primary font-medium">{t('common.current')}</span>
                  )}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <DocumentFileIcon mimeType={version.mimeType} />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-900">{version.fileName}</span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(version.fileSize)}
                    </span>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-gray-600">
                  {version.uploadedBy
                    ? `${version.uploadedBy.firstName} ${version.uploadedBy.lastName}`
                    : '-'}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-gray-600">{formatDate(version.uploadedAt)}</span>
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => onDownload(version)}
                  disabled={isDownloading}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-primary hover:bg-primary/10 rounded transition disabled:opacity-50"
                  title={t('documents.old_versions.table.download')}
                >
                  <DownloadSimple size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
